import { useState } from 'react';
import Layout from '../../../components/layout/Layout';

interface PremiacaoConfig {
  tipo: string;
  valorPremio: number;
  quantidadePremios: number;
  usarDadosCampanha: boolean; // Novo campo
}

interface SimuladorConfig {
  campanhaId: string;
  premiacoes: PremiacaoConfig[];
}

interface ResultadoSimulacao {
  valorBruto: number;
  valorLiquido: number;
  totalImpostos: number;
  totalRetencoes: number;
  custoFinalEmpresa: number;
  detalhamentoImpostos: {
    nome: string;
    aliquota: number;
    valor: number;
    baseCalculo: number;
    fundamentoLegal: string;
  }[];
  obrigacoes: string[];
  naturezaOperacao: string;
  enquadramentoTributario: string;
  alertasCompliance: string[];
}

export default function SimuladorPage() {
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState<SimuladorConfig>({
    campanhaId: '',
    premiacoes: [],
  });
  const [resultado, setResultado] = useState<ResultadoSimulacao | null>(null);
  const [comparacao, setComparacao] = useState(false);
  const [mostrarAdicionarPremiacao, setMostrarAdicionarPremiacao] = useState(false);

  // Dados mockados de campanhas - ATUALIZADO COM DADOS DISTRIBUÍDOS
  const campanhasMock = [
    { 
      id: '1', 
      nome: 'Campanha Vendas Q1 2026', 
      tipo: 'Vendas',
      publicoAlvo: ['PF', 'PJ', 'MEI', 'CLT'],
      tiposPremiacao: ['Dinheiro', 'Cartão pré-pago', 'Crédito digital (wallet)', 'Produto físico', 'Gift card'],
      dadosDistribuidos: {
        'Dinheiro': { valorPremio: 1500, quantidadePremios: 25 },
        'Cartão pré-pago': { valorPremio: 800, quantidadePremios: 40 },
        'Crédito digital (wallet)': { valorPremio: 500, quantidadePremios: 60 }
      }
    },
    { 
      id: '2', 
      nome: 'Metas Produtividade', 
      tipo: 'Metas de produtividade',
      publicoAlvo: ['CLT', 'AUTONOMO'],
      tiposPremiacao: ['Dinheiro', 'Pontos', 'Gift card', 'Produto físico'],
      dadosDistribuidos: {
        'Dinheiro': { valorPremio: 2000, quantidadePremios: 15 },
        'Pontos': { valorPremio: 300, quantidadePremios: 50 },
        'Gift card': { valorPremio: 600, quantidadePremios: 30 }
      }
    },
    { 
      id: '3', 
      nome: 'Reconhecimento Anual', 
      tipo: 'Reconhecimento interno',
      publicoAlvo: ['CLT'],
      tiposPremiacao: ['Produto físico', 'Experiência / serviço', 'Gift card', 'Viagem'],
      dadosDistribuidos: {
        'Produto físico': { valorPremio: 1200, quantidadePremios: 20 },
        'Experiência / serviço': { valorPremio: 3000, quantidadePremios: 10 },
        'Gift card': { valorPremio: 500, quantidadePremios: 35 }
      }
    },
    { 
      id: '4', 
      nome: 'Incentivo Distribuidores', 
      tipo: 'Incentivo comercial',
      publicoAlvo: ['PJ', 'DISTRIBUIDOR', 'REPRESENTANTE'],
      tiposPremiacao: ['Dinheiro', 'Produto físico', 'Viagem', 'Gift card'],
      dadosDistribuidos: {
        'Dinheiro': { valorPremio: 5000, quantidadePremios: 12 },
        'Produto físico': { valorPremio: 2500, quantidadePremios: 18 },
        'Viagem': { valorPremio: 8000, quantidadePremios: 5 }
      }
    },
  ];

  const campanhaInfo = campanhasMock.find(c => c.id === config.campanhaId);

  // Obter tipos de premiação disponíveis que ainda não foram adicionados
  const getPremiacoesDisponiveis = () => {
    if (!campanhaInfo) return [];
    const premiacoesAdicionadas = config.premiacoes.map(p => p.tipo);
    return campanhaInfo.tiposPremiacao.filter(tipo => !premiacoesAdicionadas.includes(tipo));
  };

  // Atualizar premiações quando campanha mudar
  const handleCampanhaChange = (campanhaId: string) => {
    const campanha = campanhasMock.find(c => c.id === campanhaId);
    if (campanha) {
      const premiacoesIniciais = campanha.tiposPremiacao.map(tipo => ({
        tipo,
        valorPremio: 0,
        quantidadePremios: 0,
        usarDadosCampanha: false,
      }));
      setConfig({
        campanhaId,
        premiacoes: premiacoesIniciais,
      });
    }
  };

  // Adicionar nova premiação manualmente
  const adicionarPremiacao = (tipo: string) => {
    const novaPremiacao: PremiacaoConfig = {
      tipo,
      valorPremio: 0,
      quantidadePremios: 0,
      usarDadosCampanha: false,
    };
    
    setConfig({
      ...config,
      premiacoes: [...config.premiacoes, novaPremiacao],
    });
    
    setMostrarAdicionarPremiacao(false);
  };

  // Remover premiação
  const removerPremiacao = (index: number) => {
    const novasPremiacoes = config.premiacoes.filter((_, i) => i !== index);
    setConfig({
      ...config,
      premiacoes: novasPremiacoes,
    });
  };

  const updatePremiacao = (index: number, field: 'valorPremio' | 'quantidadePremios', value: number) => {
    const novasPremiacoes = [...config.premiacoes];
    novasPremiacoes[index] = {
      ...novasPremiacoes[index],
      [field]: value,
    };
    setConfig({
      ...config,
      premiacoes: novasPremiacoes,
    });
  };

  // Nova função para alternar entre manual e dados da campanha
  const toggleUsarDadosCampanha = (index: number) => {
    const novasPremiacoes = [...config.premiacoes];
    const premiacao = novasPremiacoes[index];
    const novoEstado = !premiacao.usarDadosCampanha;
    
    if (novoEstado && campanhaInfo?.dadosDistribuidos) {
      // Usar dados da campanha
      const dadosCampanha = campanhaInfo.dadosDistribuidos[premiacao.tipo];
      if (dadosCampanha) {
        novasPremiacoes[index] = {
          ...premiacao,
          usarDadosCampanha: true,
          valorPremio: dadosCampanha.valorPremio,
          quantidadePremios: dadosCampanha.quantidadePremios,
        };
      }
    } else {
      // Voltar para manual (zerar valores)
      novasPremiacoes[index] = {
        ...premiacao,
        usarDadosCampanha: false,
        valorPremio: 0,
        quantidadePremios: 0,
      };
    }
    
    setConfig({
      ...config,
      premiacoes: novasPremiacoes,
    });
  };

  const calcularSimulacao = () => {
    // Calcular totais de todas as premiações
    let valorBrutoTotal = 0;
    let quantidadeTotal = 0;

    config.premiacoes.forEach(premiacao => {
      valorBrutoTotal += premiacao.valorPremio * premiacao.quantidadePremios;
      quantidadeTotal += premiacao.quantidadePremios;
    });

    // Lógica de cálculo tributário
    let valorBruto = valorBrutoTotal / quantidadeTotal || 0; // Valor médio por premiado
    let impostos: any[] = [];
    let obrigacoes: string[] = [];
    let alertas: string[] = [];
    let natureza = '';
    let enquadramento = '';

    // Determinar natureza jurídica
    if (config.tipoCampanha === 'Brinde') {
      natureza = 'Distribuição gratuita';
    } else if (config.tipoCampanha === 'Bônus eventual') {
      natureza = 'Premiação eventual';
    } else {
      natureza = 'Incentivo comercial';
    }

    // Cálculos simplificados (exemplo com PF)
    let aliquotaIRRF = 0;
    let parcelaIRRF = 0;
    
    if (valorBruto > 4664.68) {
      aliquotaIRRF = 27.5;
      parcelaIRRF = 869.36;
    } else if (valorBruto > 3751.06) {
      aliquotaIRRF = 22.5;
      parcelaIRRF = 636.13;
    } else if (valorBruto > 2826.66) {
      aliquotaIRRF = 15;
      parcelaIRRF = 354.80;
    } else if (valorBruto > 2259.21) {
      aliquotaIRRF = 7.5;
      parcelaIRRF = 169.44;
    }

    if (aliquotaIRRF > 0) {
      const valorIRRF = (valorBruto * aliquotaIRRF / 100) - parcelaIRRF;
      impostos.push({
        nome: 'IRRF - Imposto de Renda Retido na Fonte',
        aliquota: aliquotaIRRF,
        valor: valorIRRF,
        baseCalculo: valorBruto,
        fundamentoLegal: 'Lei nº 13.149/2015, Art. 3º - Tabela progressiva do IR',
      });
    }

    obrigacoes.push('eSocial - Evento S-1200 (Remuneração)');
    obrigacoes.push('Informe de Rendimentos anual');
    obrigacoes.push('DIRF - Declaração do Imposto de Renda Retido na Fonte');
    enquadramento = 'Rendimento de pessoa física';

    // Multiplicar por quantidade total de premiados
    const custoFinal = valorBrutoTotal;
    const totalImpostosGeral = impostos.reduce((sum, imp) => sum + imp.valor, 0) * quantidadeTotal;

    setResultado({
      valorBruto: valorBrutoTotal,
      valorLiquido: valorBrutoTotal - totalImpostosGeral,
      totalImpostos: totalImpostosGeral,
      totalRetencoes: totalImpostosGeral,
      custoFinalEmpresa: custoFinal,
      detalhamentoImpostos: impostos,
      obrigacoes,
      naturezaOperacao: natureza,
      enquadramentoTributario: enquadramento,
      alertasCompliance: alertas,
    });

    setStep(2);
  };

  const gerarPDF = () => {
    alert('Funcionalidade de exportação PDF será implementada em breve!');
  };

  const gerarExcel = () => {
    alert('Funcionalidade de exportação Excel será implementada em breve!');
  };

  const compararModelos = () => {
    setComparacao(true);
  };

  const isFormValid = () => {
    if (!config.campanhaId) return false;
    return config.premiacoes.some(p => p.valorPremio > 0 && p.quantidadePremios > 0);
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
              <i className="ri-calculator-line text-white text-xl"></i>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Simulador Tributário</h1>
              <p className="text-sm text-gray-600">Calcule o impacto tributário completo da sua campanha de incentivo</p>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between max-w-3xl mx-auto">
            {[
              { num: 1, label: 'Configuração', icon: 'settings-3-line' },
              { num: 2, label: 'Resultado', icon: 'file-chart-line' },
              { num: 3, label: 'Detalhamento', icon: 'file-list-3-line' },
              { num: 4, label: 'Exportação', icon: 'download-line' },
            ].map((stepItem, idx) => (
              <div key={stepItem.num} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    stepItem.num <= step
                      ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}>
                    <i className={`ri-${stepItem.icon} text-xl`}></i>
                  </div>
                  <span className={`text-xs mt-2 font-medium ${
                    stepItem.num <= step ? 'text-purple-600' : 'text-gray-500'
                  }`}>
                    {stepItem.label}
                  </span>
                </div>
                {idx < 3 && (
                  <div className={`flex-1 h-0.5 mx-2 ${
                    idx + 1 < step ? 'bg-gradient-to-r from-purple-500 to-pink-500' : 'bg-gray-300'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Configuração */}
        {step === 1 && (
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                <i className="ri-settings-3-line text-white text-xl"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Configuração da Simulação</h2>
                <p className="text-gray-600">Selecione a campanha e defina os parâmetros</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Campanha */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Campanha de Incentivo *
                </label>
                <select
                  value={config.campanhaId}
                  onChange={(e) => handleCampanhaChange(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                >
                  <option value="">Selecione uma campanha</option>
                  {campanhasMock.map((campanha) => (
                    <option key={campanha.id} value={campanha.id}>
                      {campanha.nome}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Selecione a campanha cadastrada na plataforma
                </p>
              </div>

              {/* Info da Campanha */}
              {campanhaInfo && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-information-line text-blue-600 text-xl mt-0.5"></i>
                    <div className="flex-1">
                      <h4 className="font-semibold text-blue-900 mb-2">Informações da Campanha</h4>
                      
                      <div className="space-y-2">
                        <div>
                          <p className="text-xs font-medium text-blue-700 mb-1">Tipo de Campanha:</p>
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium">
                            <i className="ri-flag-line"></i>
                            {campanhaInfo.tipo}
                          </span>
                        </div>

                        <div>
                          <p className="text-xs font-medium text-blue-700 mb-1">Forma de Cálculo:</p>
                          <div className="flex gap-2">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white text-blue-700 rounded-lg text-sm font-medium border border-blue-200">
                              <i className="ri-money-dollar-circle-line"></i>
                              Bruto
                            </span>
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white text-blue-700 rounded-lg text-sm font-medium border border-blue-200">
                              <i className="ri-hand-coin-line"></i>
                              Líquido
                            </span>
                          </div>
                        </div>

                        <div>
                          <p className="text-xs font-medium text-blue-700 mb-1">Público-alvo disponível:</p>
                          <div className="flex flex-wrap gap-2">
                            {campanhaInfo.publicoAlvo.map((tipo) => (
                              <span
                                key={tipo}
                                className="inline-flex items-center gap-1.5 px-3 py-1 bg-white text-blue-700 rounded-lg text-sm font-medium border border-blue-200"
                              >
                                <i className={`${getTipoIcon(tipo)}`}></i>
                                {tipo}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div>
                          <p className="text-xs font-medium text-blue-700 mb-1">Tipos de premiação disponíveis:</p>
                          <div className="flex flex-wrap gap-2">
                            {campanhaInfo.tiposPremiacao.map((tipo) => (
                              <span
                                key={tipo}
                                className="inline-flex items-center gap-1.5 px-3 py-1 bg-white text-blue-700 rounded-lg text-sm font-medium border border-blue-200"
                              >
                                <i className={`${getPremiacaoIcon(tipo)}`}></i>
                                {tipo}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tipos de Premiação - Segregados */}
              {config.premiacoes.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <i className="ri-gift-line text-purple-600 text-xl"></i>
                      <h3 className="text-lg font-semibold text-gray-900">Tipos de premiação disponíveis:</h3>
                    </div>
                    
                    {/* Botão para adicionar nova premiação */}
                    {getPremiacoesDisponiveis().length > 0 && (
                      <button
                        onClick={() => setMostrarAdicionarPremiacao(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 transition-all whitespace-nowrap"
                      >
                        <i className="ri-add-line text-lg"></i>
                        Adicionar Premiação
                      </button>
                    )}
                  </div>
                  
                  {config.premiacoes.map((premiacao, index) => {
                    const dadosCampanha = campanhaInfo?.dadosDistribuidos?.[premiacao.tipo];
                    
                    return (
                      <div key={index} className="border-2 border-gray-200 rounded-xl p-6 hover:border-purple-300 transition-all">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                              <i className={`${getPremiacaoIcon(premiacao.tipo)} text-white text-lg`}></i>
                            </div>
                            <h4 className="text-lg font-semibold text-gray-900">{premiacao.tipo}</h4>
                          </div>

                          <div className="flex items-center gap-2">
                            {/* Toggle para usar dados da campanha */}
                            {dadosCampanha && (
                              <button
                                onClick={() => toggleUsarDadosCampanha(index)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                                  premiacao.usarDadosCampanha
                                    ? 'bg-blue-100 text-blue-700 border-2 border-blue-300'
                                    : 'bg-gray-100 text-gray-700 border-2 border-gray-300 hover:bg-gray-200'
                                }`}
                              >
                                <i className={`${premiacao.usarDadosCampanha ? 'ri-database-2-line' : 'ri-edit-line'} text-lg`}></i>
                                {premiacao.usarDadosCampanha ? 'Usando dados da campanha' : 'Inserir valores manualmente'}
                              </button>
                            )}
                            
                            {/* Botão para remover premiação */}
                            <button
                              onClick={() => removerPremiacao(index)}
                              className="w-9 h-9 flex items-center justify-center bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all"
                              title="Remover premiação"
                            >
                              <i className="ri-delete-bin-line text-lg"></i>
                            </button>
                          </div>
                        </div>

                        {/* Mostrar dados da campanha disponíveis */}
                        {dadosCampanha && !premiacao.usarDadosCampanha && (
                          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-start gap-2">
                              <i className="ri-information-line text-blue-600 text-lg mt-0.5"></i>
                              <div className="flex-1">
                                <p className="text-sm font-medium text-blue-900 mb-1">Dados já distribuídos nesta campanha:</p>
                                <div className="flex gap-4 text-sm text-blue-700">
                                  <span>💰 R$ {dadosCampanha.valorPremio.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} por premiado</span>
                                  <span>👥 {dadosCampanha.quantidadePremios} premiados</span>
                                  <span>📊 Total: R$ {(dadosCampanha.valorPremio * dadosCampanha.quantidadePremios).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Indicador quando está usando dados da campanha */}
                        {premiacao.usarDadosCampanha && (
                          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center gap-2">
                              <i className="ri-checkbox-circle-line text-green-600 text-lg"></i>
                              <p className="text-sm font-medium text-green-900">
                                Valores carregados automaticamente dos dados já distribuídos na campanha
                              </p>
                            </div>
                          </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Valor do Prêmio */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Valor do Prêmio (R$) *
                            </label>
                            <input
                              type="number"
                              value={premiacao.valorPremio || ''}
                              onChange={(e) => updatePremiacao(index, 'valorPremio', parseFloat(e.target.value) || 0)}
                              disabled={premiacao.usarDadosCampanha}
                              className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                                premiacao.usarDadosCampanha ? 'bg-gray-100 cursor-not-allowed' : ''
                              }`}
                              placeholder="0,00"
                              min="0"
                              step="0.01"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Valor do prêmio por premiado
                            </p>
                          </div>

                          {/* Quantidade de Premiados */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Quantidade de Premiados *
                            </label>
                            <input
                              type="number"
                              value={premiacao.quantidadePremios || ''}
                              onChange={(e) => updatePremiacao(index, 'quantidadePremios', parseInt(e.target.value) || 0)}
                              disabled={premiacao.usarDadosCampanha}
                              className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                                premiacao.usarDadosCampanha ? 'bg-gray-100 cursor-not-allowed' : ''
                              }`}
                              placeholder="0"
                              min="0"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Número total de pessoas que receberão o prêmio
                            </p>
                          </div>
                        </div>

                        {/* Subtotal */}
                        {premiacao.valorPremio > 0 && premiacao.quantidadePremios > 0 && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-600">Subtotal:</span>
                              <span className="text-lg font-bold text-purple-600">
                                R$ {(premiacao.valorPremio * premiacao.quantidadePremios).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Total Geral */}
                  {config.premiacoes.some(p => p.valorPremio > 0 && p.quantidadePremios > 0) && (
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-300 rounded-xl p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-purple-700 mb-1">Total Geral da Campanha</p>
                          <p className="text-xs text-purple-600">
                            {config.premiacoes.reduce((sum, p) => sum + p.quantidadePremios, 0)} premiado(s) no total
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-3xl font-bold text-purple-600">
                            R$ {config.premiacoes.reduce((sum, p) => sum + (p.valorPremio * p.quantidadePremios), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Botão Calcular */}
            <div className="mt-8 flex justify-end">
              <button
                onClick={calcularSimulacao}
                disabled={!isFormValid()}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
              >
                <i className="ri-calculator-line text-xl"></i>
                Calcular Simulação
              </button>
            </div>
          </div>
        )}

        {/* Modal para adicionar nova premiação */}
        {mostrarAdicionarPremiacao && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Adicionar Tipo de Premiação</h2>
                  <p className="text-sm text-gray-600 mt-1">Selecione um tipo de premiação disponível na campanha</p>
                </div>
                <button
                  onClick={() => setMostrarAdicionarPremiacao(false)}
                  className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg"
                >
                  <i className="ri-close-line text-xl text-gray-500"></i>
                </button>
              </div>

              <div className="p-6">
                {getPremiacoesDisponiveis().length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {getPremiacoesDisponiveis().map((tipo) => {
                      const dadosCampanha = campanhaInfo?.dadosDistribuidos?.[tipo];
                      
                      return (
                        <button
                          key={tipo}
                          onClick={() => adicionarPremiacao(tipo)}
                          className="border-2 border-gray-200 rounded-xl p-5 hover:border-purple-500 hover:bg-purple-50 transition-all text-left group"
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                              <i className={`${getPremiacaoIcon(tipo)} text-white text-xl`}></i>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-gray-900 mb-1">{tipo}</h4>
                              
                              {dadosCampanha ? (
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2 text-xs text-blue-600">
                                    <i className="ri-database-2-line"></i>
                                    <span className="font-medium">Dados disponíveis</span>
                                  </div>
                                  <div className="text-xs text-gray-600">
                                    <div>💰 R$ {dadosCampanha.valorPremio.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
                                    <div>👥 {dadosCampanha.quantidadePremios} premiados</div>
                                  </div>
                                </div>
                              ) : (
                                <p className="text-xs text-gray-500">Sem dados distribuídos</p>
                              )}
                            </div>
                            <i className="ri-add-circle-line text-2xl text-purple-600 group-hover:scale-110 transition-transform"></i>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-checkbox-circle-line text-gray-400 text-3xl"></i>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">Todos os tipos já foram adicionados</h3>
                    <p className="text-sm text-gray-600">
                      Todos os tipos de premiação disponíveis na campanha já estão incluídos na simulação.
                    </p>
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => setMostrarAdicionarPremiacao(false)}
                  className="w-full px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-white transition-all whitespace-nowrap"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Etapa 2 - Resultado Resumido */}
        {step === 2 && resultado && (
          <div className="space-y-6">
            {/* Cards de Resumo */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-blue-100 text-sm">Valor Bruto</span>
                  <i className="ri-money-dollar-circle-line text-2xl text-blue-100"></i>
                </div>
                <div className="text-3xl font-bold">
                  R$ {resultado.valorBruto.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
                <div className="text-xs text-blue-100 mt-1">
                  {config.premiacoes.reduce((sum, p) => sum + p.quantidadePremios, 0)} premiado(s)
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-green-100 text-sm">Valor Líquido</span>
                  <i className="ri-hand-coin-line text-2xl text-green-100"></i>
                </div>
                <div className="text-3xl font-bold">
                  R$ {resultado.valorLiquido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
                <div className="text-xs text-green-100 mt-1">
                  Após impostos
                </div>
              </div>

              <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-red-100 text-sm">Total de Impostos</span>
                  <i className="ri-file-text-line text-2xl text-red-100"></i>
                </div>
                <div className="text-3xl font-bold">
                  R$ {resultado.totalImpostos.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
                <div className="text-xs text-red-100 mt-1">
                  {((resultado.totalImpostos / resultado.valorBruto) * 100).toFixed(2)}% do valor bruto
                </div>
              </div>
            </div>

            {/* Detalhamento por Tipo de Premiação */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-gift-line text-purple-600"></i>
                Detalhamento por Tipo de Premiação
              </h3>
              <div className="space-y-4">
                {config.premiacoes.filter(p => p.quantidadePremios > 0).map((premiacao, index) => {
                  // Calcular impostos para este tipo de premiação
                  const valorUnitario = premiacao.valorPremio;
                  const valorTotal = valorUnitario * premiacao.quantidadePremios;
                  
                  // Impostos da empresa (exemplo: encargos sociais - 20%)
                  const encargosEmpresa = valorTotal * 0.20;
                  const custoTotalEmpresa = valorTotal + encargosEmpresa;
                  
                  // Função para calcular impostos por tipo de público
                  const calcularImpostosPorPublico = (valorUnitario: number, tipoPublico: string) => {
                    let aliquota = 0;
                    let parcela = 0;
                    let descricao = '';
                    
                    switch (tipoPublico) {
                      case 'PF':
                        // Cálculo de IRRF para Pessoa Física
                        if (valorUnitario > 4664.68) {
                          aliquota = 27.5;
                          parcela = 869.36;
                        } else if (valorUnitario > 3751.06) {
                          aliquota = 22.5;
                          parcela = 636.13;
                        } else if (valorUnitario > 2826.66) {
                          aliquota = 15;
                          parcela = 354.80;
                        } else if (valorUnitario > 2259.21) {
                          aliquota = 7.5;
                          parcela = 169.44;
                        }
                        descricao = 'IRRF';
                        break;
                        
                      case 'PJ':
                        // Pessoa Jurídica - Simples Nacional ou Lucro Presumido
                        aliquota = 11.33;
                        parcela = 0;
                        descricao = 'ISS + COFINS + PIS';
                        break;
                        
                      case 'MEI':
                        // MEI - DAS fixo mensal (simulação proporcional)
                        aliquota = 1.34;
                        parcela = 0;
                        descricao = 'DAS (proporcional)';
                        break;
                        
                      case 'CLT':
                        // CLT - IRRF + INSS
                        if (valorUnitario > 4664.68) {
                          aliquota = 27.5;
                          parcela = 869.36;
                        } else if (valorUnitario > 3751.06) {
                          aliquota = 22.5;
                          parcela = 636.13;
                        } else if (valorUnitario > 2826.66) {
                          aliquota = 15;
                          parcela = 354.80;
                        } else if (valorUnitario > 2259.21) {
                          aliquota = 7.5;
                          parcela = 169.44;
                        }
                        // Adicionar INSS (11%)
                        const inss = Math.min(valorUnitario * 0.11, 908.85); // Teto INSS
                        parcela += inss;
                        descricao = 'IRRF + INSS';
                        break;
                        
                      case 'AUTONOMO':
                        // Autônomo - IRRF + INSS
                        if (valorUnitario > 4664.68) {
                          aliquota = 27.5;
                          parcela = 869.36;
                        } else if (valorUnitario > 3751.06) {
                          aliquota = 22.5;
                          parcela = 636.13;
                        } else if (valorUnitario > 2826.66) {
                          aliquota = 15;
                          parcela = 354.80;
                        } else if (valorUnitario > 2259.21) {
                          aliquota = 7.5;
                          parcela = 169.44;
                        }
                        const inssAutonomo = valorUnitario * 0.11;
                        parcela += inssAutonomo;
                        descricao = 'IRRF + INSS (11%)';
                        break;
                        
                      default:
                        aliquota = 0;
                        parcela = 0;
                        descricao = 'Não aplicável';
                    }
                    
                    const impostoCalculado = aliquota > 0 ? (valorUnitario * aliquota / 100) - parcela : 0;
                    const impostoFinal = Math.max(impostoCalculado, 0);
                    
                    return {
                      aliquota,
                      imposto: impostoFinal,
                      valorLiquido: valorUnitario - impostoFinal,
                      descricao
                    };
                  };
                  
                  // Obter públicos-alvo da campanha selecionada
                  const publicosAlvo = campanhaInfo?.publicoAlvo || ['PF'];
                  
                  return (
                    <div key={index} className="border-2 border-gray-200 rounded-xl p-5 hover:border-purple-300 transition-all">
                      {/* Header */}
                      <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                            <i className={`${getPremiacaoIcon(premiacao.tipo)} text-white text-xl`}></i>
                          </div>
                          <div>
                            <p className="font-bold text-gray-900 text-lg">{premiacao.tipo}</p>
                            <p className="text-sm text-gray-500">{premiacao.quantidadePremios} premiado(s)</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500 mb-1">Valor Total Bruto</p>
                          <p className="text-2xl font-bold text-purple-600">
                            R$ {valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                      </div>

                      {/* Detalhamento em Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Coluna 1: Impostos do Premiado - SEGREGADO POR PÚBLICO-ALVO */}
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                              <i className="ri-user-line text-white text-sm"></i>
                            </div>
                            <h4 className="font-semibold text-orange-900">Impostos do Premiado</h4>
                          </div>
                          
                          {/* Segregação por Público-Alvo */}
                          <div className="space-y-4">
                            {publicosAlvo.map((publico) => {
                              const calculos = calcularImpostosPorPublico(valorUnitario, publico);
                              const impostoTotal = calculos.imposto * premiacao.quantidadePremios;
                              const valorLiquidoTotal = calculos.valorLiquido * premiacao.quantidadePremios;
                              
                              return (
                                <div key={publico} className="bg-white border border-orange-300 rounded-lg p-3">
                                  {/* Cabeçalho do Público */}
                                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-orange-200">
                                    <div className="w-6 h-6 bg-orange-100 rounded flex items-center justify-center">
                                      <i className={`${getTipoIcon(publico)} text-orange-600 text-sm`}></i>
                                    </div>
                                    <span className="font-semibold text-orange-900 text-sm">{publico}</span>
                                  </div>
                                  
                                  {/* Detalhes do Cálculo */}
                                  <div className="space-y-2">
                                    <div className="flex justify-between items-center">
                                      <span className="text-xs text-gray-600">Valor Bruto (unitário):</span>
                                      <span className="text-sm font-semibold text-gray-900">
                                        R$ {valorUnitario.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                      </span>
                                    </div>
                                    
                                    <div className="flex justify-between items-center">
                                      <span className="text-xs text-gray-600">{calculos.descricao}:</span>
                                      <span className="text-sm font-semibold text-red-600">
                                        - R$ {calculos.imposto.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                      </span>
                                    </div>
                                    
                                    {calculos.aliquota > 0 && (
                                      <div className="flex justify-between items-center">
                                        <span className="text-xs text-gray-600">Alíquota efetiva:</span>
                                        <span className="text-xs font-medium text-gray-700">
                                          {((calculos.imposto / valorUnitario) * 100).toFixed(2)}%
                                        </span>
                                      </div>
                                    )}
                                    
                                    <div className="pt-2 border-t border-orange-200">
                                      <div className="flex justify-between items-center">
                                        <span className="text-xs font-semibold text-gray-700">Valor Líquido (unitário):</span>
                                        <span className="text-sm font-bold text-green-600">
                                          R$ {calculos.valorLiquido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                        </span>
                                      </div>
                                    </div>
                                    
                                    <div className="pt-2 border-t border-orange-200 mt-2 bg-orange-50 -mx-3 -mb-3 px-3 py-2 rounded-b-lg">
                                      <div className="flex justify-between items-center mb-1">
                                        <span className="text-xs font-semibold text-gray-700">Total Impostos ({publico}):</span>
                                        <span className="text-sm font-bold text-red-600">
                                          R$ {impostoTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                        </span>
                                      </div>
                                      <div className="flex justify-between items-center">
                                        <span className="text-xs font-semibold text-gray-700">Total Líquido ({publico}):</span>
                                        <span className="text-sm font-bold text-green-600">
                                          R$ {valorLiquidoTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Coluna 2: Impostos da Empresa */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                              <i className="ri-building-line text-white text-sm"></i>
                            </div>
                            <h4 className="font-semibold text-blue-900">Impostos da Empresa</h4>
                          </div>
                          
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-gray-600">Valor dos Prêmios:</span>
                              <span className="text-sm font-semibold text-gray-900">
                                R$ {valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </span>
                            </div>
                            
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-gray-600">Encargos (20%):</span>
                              <span className="text-sm font-semibold text-red-600">
                                + R$ {encargosEmpresa.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </span>
                            </div>
                            
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-gray-600">INSS Patronal:</span>
                              <span className="text-sm font-semibold text-gray-500">
                                Incluso
                              </span>
                            </div>
                            
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-gray-600">FGTS:</span>
                              <span className="text-sm font-semibold text-gray-500">
                                Incluso
                              </span>
                            </div>
                            
                            <div className="pt-2 border-t border-blue-300 mt-2">
                              <div className="flex justify-between items-center">
                                <span className="text-xs font-semibold text-gray-700">Custo Total Empresa:</span>
                                <span className="text-sm font-bold text-blue-600">
                                  R$ {custoTotalEmpresa.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Resumo Final */}
                      <div className="mt-4 pt-4 border-t border-gray-200 bg-gray-50 rounded-lg p-3">
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Valor Unitário</p>
                            <p className="text-sm font-bold text-gray-900">
                              R$ {valorUnitario.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Quantidade</p>
                            <p className="text-sm font-bold text-gray-900">
                              {premiacao.quantidadePremios}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Públicos-Alvo</p>
                            <p className="text-sm font-bold text-purple-600">
                              {publicosAlvo.length} tipo(s)
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Custo Final */}
            <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl p-8 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-purple-100 mb-2">Custo Total da Empresa</div>
                  <div className="text-4xl font-bold">
                    R$ {resultado.custoFinalEmpresa.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </div>
                  <div className="text-sm text-purple-100 mt-2">
                    Para {config.premiacoes.reduce((sum, p) => sum + p.quantidadePremios, 0)} premiado(s)
                  </div>
                </div>
                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="ri-building-line text-4xl"></i>
                </div>
              </div>
            </div>

            {/* Informações Adicionais */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-information-line text-blue-600"></i>
                  Natureza da Operação
                </h3>
                <p className="text-gray-700">{resultado.naturezaOperacao}</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-file-shield-line text-purple-600"></i>
                  Enquadramento Tributário
                </h3>
                <p className="text-gray-700">{resultado.enquadramentoTributario}</p>
              </div>
            </div>

            {/* Alertas de Compliance */}
            {resultado.alertasCompliance.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                <h3 className="font-semibold text-yellow-900 mb-3 flex items-center gap-2">
                  <i className="ri-alert-line text-yellow-600"></i>
                  Alertas de Compliance
                </h3>
                <ul className="space-y-2">
                  {resultado.alertasCompliance.map((alerta, idx) => (
                    <li key={idx} className="text-yellow-800 text-sm">{alerta}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Botões de Ação */}
            <div className="flex justify-between gap-3">
              <button
                onClick={() => setStep(1)}
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 whitespace-nowrap"
              >
                Voltar
              </button>
              <div className="flex gap-3">
                <button
                  onClick={compararModelos}
                  className="px-6 py-2.5 border border-purple-600 text-purple-600 rounded-lg font-medium hover:bg-purple-50 whitespace-nowrap"
                >
                  Comparar Modelos
                </button>
                <button
                  onClick={() => setStep(3)}
                  className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 whitespace-nowrap"
                >
                  Ver Detalhamento
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Etapa 3 - Detalhamento Tributário */}
        {step === 3 && resultado && (
          <div className="space-y-6">
            {/* Header do Detalhamento */}
            <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl p-6 text-white">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                  <i className="ri-file-list-3-line text-4xl"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-1">Detalhamento e Conformidade Tributária</h2>
                  <p className="text-purple-100">Análise comparativa entre impostos calculados e recolhidos</p>
                </div>
              </div>
            </div>

            {/* Resumo Geral de Conformidade */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl shadow-sm border-2 border-blue-200 p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Impostos Calculados</span>
                  <i className="ri-calculator-line text-blue-600 text-xl"></i>
                </div>
                <div className="text-2xl font-bold text-blue-600">
                  R$ {resultado.totalImpostos.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border-2 border-green-200 p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Impostos Recolhidos</span>
                  <i className="ri-checkbox-circle-line text-green-600 text-xl"></i>
                </div>
                <div className="text-2xl font-bold text-green-600">
                  R$ {(resultado.totalImpostos * 0.95).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border-2 border-orange-200 p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Diferença</span>
                  <i className="ri-alert-line text-orange-600 text-xl"></i>
                </div>
                <div className="text-2xl font-bold text-orange-600">
                  R$ {(resultado.totalImpostos * 0.05).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border-2 border-purple-200 p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Conformidade</span>
                  <i className="ri-shield-check-line text-purple-600 text-xl"></i>
                </div>
                <div className="text-2xl font-bold text-purple-600">95%</div>
              </div>
            </div>

            {/* Detalhamento por Tipo de Premiação e Público-Alvo */}
            {config.premiacoes.filter(p => p.quantidadePremios > 0).map((premiacao, premiacaoIndex) => {
              const valorUnitario = premiacao.valorPremio;
              const valorTotal = valorUnitario * premiacao.quantidadePremios;
              const publicosAlvo = campanhaInfo?.publicoAlvo || ['PF'];

              // Função para calcular impostos por tipo de público
              const calcularImpostosPorPublico = (valorUnitario: number, tipoPublico: string) => {
                let aliquota = 0;
                let parcela = 0;
                let descricao = '';
                let impostos: any[] = [];
                
                switch (tipoPublico) {
                  case 'PF':
                    if (valorUnitario > 4664.68) {
                      aliquota = 27.5;
                      parcela = 869.36;
                    } else if (valorUnitario > 3751.06) {
                      aliquota = 22.5;
                      parcela = 636.13;
                    } else if (valorUnitario > 2826.66) {
                      aliquota = 15;
                      parcela = 354.80;
                    } else if (valorUnitario > 2259.21) {
                      aliquota = 7.5;
                      parcela = 169.44;
                    }
                    descricao = 'IRRF - Imposto de Renda Retido na Fonte';
                    const irrfPF = Math.max((valorUnitario * aliquota / 100) - parcela, 0);
                    impostos.push({
                      nome: 'IRRF',
                      descricao: 'Imposto de Renda Retido na Fonte',
                      aliquota: aliquota,
                      baseCalculo: valorUnitario,
                      valorCalculado: irrfPF,
                      valorRecolhido: irrfPF * 0.98, // 98% recolhido
                      diferenca: irrfPF * 0.02,
                      status: 'atencao'
                    });
                    break;
                    
                  case 'PJ':
                    impostos.push({
                      nome: 'ISS',
                      descricao: 'Imposto Sobre Serviços',
                      aliquota: 5.0,
                      baseCalculo: valorUnitario,
                      valorCalculado: valorUnitario * 0.05,
                      valorRecolhido: valorUnitario * 0.05,
                      diferenca: 0,
                      status: 'conforme'
                    });
                    impostos.push({
                      nome: 'COFINS',
                      descricao: 'Contribuição para Financiamento da Seguridade Social',
                      aliquota: 3.0,
                      baseCalculo: valorUnitario,
                      valorCalculado: valorUnitario * 0.03,
                      valorRecolhido: valorUnitario * 0.03,
                      diferenca: 0,
                      status: 'conforme'
                    });
                    impostos.push({
                      nome: 'PIS',
                      descricao: 'Programa de Integração Social',
                      aliquota: 0.65,
                      baseCalculo: valorUnitario,
                      valorCalculado: valorUnitario * 0.0065,
                      valorRecolhido: valorUnitario * 0.0065,
                      diferenca: 0,
                      status: 'conforme'
                    });
                    impostos.push({
                      nome: 'CSLL',
                      descricao: 'Contribuição Social sobre o Lucro Líquido',
                      aliquota: 2.88,
                      baseCalculo: valorUnitario,
                      valorCalculado: valorUnitario * 0.0288,
                      valorRecolhido: valorUnitario * 0.0288,
                      diferenca: 0,
                      status: 'conforme'
                    });
                    break;
                    
                  case 'MEI':
                    impostos.push({
                      nome: 'DAS',
                      descricao: 'Documento de Arrecadação do Simples Nacional',
                      aliquota: 1.34,
                      baseCalculo: valorUnitario,
                      valorCalculado: valorUnitario * 0.0134,
                      valorRecolhido: valorUnitario * 0.0134,
                      diferenca: 0,
                      status: 'conforme'
                    });
                    break;
                    
                  case 'CLT':
                    if (valorUnitario > 4664.68) {
                      aliquota = 27.5;
                      parcela = 869.36;
                    } else if (valorUnitario > 3751.06) {
                      aliquota = 22.5;
                      parcela = 636.13;
                    } else if (valorUnitario > 2826.66) {
                      aliquota = 15;
                      parcela = 354.80;
                    } else if (valorUnitario > 2259.21) {
                      aliquota = 7.5;
                      parcela = 169.44;
                    }
                    const irrfCLT = Math.max((valorUnitario * aliquota / 100) - parcela, 0);
                    impostos.push({
                      nome: 'IRRF',
                      descricao: 'Imposto de Renda Retido na Fonte',
                      aliquota: aliquota,
                      baseCalculo: valorUnitario,
                      valorCalculado: irrfCLT,
                      valorRecolhido: irrfCLT * 0.95,
                      diferenca: irrfCLT * 0.05,
                      status: 'atencao'
                    });
                    const inssCLT = Math.min(valorUnitario * 0.11, 908.85);
                    impostos.push({
                      nome: 'INSS',
                      descricao: 'Instituto Nacional do Seguro Social',
                      aliquota: 11.0,
                      baseCalculo: valorUnitario,
                      valorCalculado: inssCLT,
                      valorRecolhido: inssCLT,
                      diferenca: 0,
                      status: 'conforme'
                    });
                    break;
                    
                  case 'AUTONOMO':
                    if (valorUnitario > 4664.68) {
                      aliquota = 27.5;
                      parcela = 869.36;
                    } else if (valorUnitario > 3751.06) {
                      aliquota = 22.5;
                      parcela = 636.13;
                    } else if (valorUnitario > 2826.66) {
                      aliquota = 15;
                      parcela = 354.80;
                    } else if (valorUnitario > 2259.21) {
                      aliquota = 7.5;
                      parcela = 169.44;
                    }
                    const irrfAut = Math.max((valorUnitario * aliquota / 100) - parcela, 0);
                    impostos.push({
                      nome: 'IRRF',
                      descricao: 'Imposto de Renda Retido na Fonte',
                      aliquota: aliquota,
                      baseCalculo: valorUnitario,
                      valorCalculado: irrfAut,
                      valorRecolhido: irrfAut * 0.92,
                      diferenca: irrfAut * 0.08,
                      status: 'alerta'
                    });
                    const inssAut = valorUnitario * 0.11;
                    impostos.push({
                      nome: 'INSS',
                      descricao: 'Instituto Nacional do Seguro Social',
                      aliquota: 11.0,
                      baseCalculo: valorUnitario,
                      valorCalculado: inssAut,
                      valorRecolhido: inssAut * 0.97,
                      diferenca: inssAut * 0.03,
                      status: 'atencao'
                    });
                    break;
                }
                
                return impostos;
              };

              return (
                <div key={premiacaoIndex} className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                  {/* Header da Premiação */}
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-5 text-white">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                          <i className={`${getPremiacaoIcon(premiacao.tipo)} text-2xl`}></i>
                        </div>
                        <div>
                          <h3 className="text-xl font-bold">{premiacao.tipo}</h3>
                          <p className="text-purple-100 text-sm">{premiacao.quantidadePremios} premiado(s) • R$ {valorUnitario.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} cada</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-purple-100 text-sm mb-1">Valor Total</p>
                        <p className="text-2xl font-bold">R$ {valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                      </div>
                    </div>
                  </div>

                  {/* Detalhamento por Público-Alvo */}
                  <div className="p-6 space-y-6">
                    {publicosAlvo.map((publico, publicoIndex) => {
                      const impostosPublico = calcularImpostosPorPublico(valorUnitario, publico);
                      const totalCalculado = impostosPublico.reduce((sum, imp) => sum + imp.valorCalculado, 0);
                      const totalRecolhido = impostosPublico.reduce((sum, imp) => sum + imp.valorRecolhido, 0);
                      const totalDiferenca = totalCalculado - totalRecolhido;
                      const percentualConformidade = totalCalculado > 0 ? (totalRecolhido / totalCalculado) * 100 : 100;
                      
                      // Determinar status geral
                      let statusGeral = 'conforme';
                      if (percentualConformidade < 90) statusGeral = 'alerta';
                      else if (percentualConformidade < 98) statusGeral = 'atencao';

                      return (
                        <div key={publicoIndex} className="border-2 border-gray-200 rounded-xl overflow-hidden">
                          {/* Header do Público */}
                          <div className="bg-gray-50 px-5 py-4 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                  publico === 'PF' ? 'bg-blue-100' :
                                  publico === 'PJ' ? 'bg-purple-100' :
                                  publico === 'MEI' ? 'bg-green-100' :
                                  publico === 'CLT' ? 'bg-orange-100' :
                                  'bg-teal-100'
                                }`}>
                                  <i className={`${getTipoIcon(publico)} ${
                                    publico === 'PF' ? 'text-blue-600' :
                                    publico === 'PJ' ? 'text-purple-600' :
                                    publico === 'MEI' ? 'text-green-600' :
                                    publico === 'CLT' ? 'text-orange-600' :
                                    'text-teal-600'
                                  } text-xl`}></i>
                                </div>
                                <div>
                                  <h4 className="font-bold text-gray-900 text-lg">{publico}</h4>
                                  <p className="text-sm text-gray-600">
                                    {publico === 'PF' ? 'Pessoa Física' :
                                     publico === 'PJ' ? 'Pessoa Jurídica' :
                                     publico === 'MEI' ? 'Microempreendedor Individual' :
                                     publico === 'CLT' ? 'Trabalhador CLT' :
                                     'Autônomo (RPA)'}
                                  </p>
                                </div>
                              </div>
                              
                              {/* Badge de Status */}
                              <div className={`px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 ${
                                statusGeral === 'conforme' ? 'bg-green-100 text-green-700' :
                                statusGeral === 'atencao' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                <i className={`${
                                  statusGeral === 'conforme' ? 'ri-checkbox-circle-line' :
                                  statusGeral === 'atencao' ? 'ri-error-warning-line' :
                                  'ri-alert-line'
                                } text-lg`}></i>
                                {statusGeral === 'conforme' ? 'Conforme' :
                                 statusGeral === 'atencao' ? 'Atenção' :
                                 'Alerta'}
                              </div>
                            </div>
                          </div>

                          {/* Tabela de Impostos */}
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Imposto</th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Base de Cálculo</th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Alíquota</th>
                                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Calculado</th>
                                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Recolhido</th>
                                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Diferença</th>
                                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Status</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {impostosPublico.map((imposto, impostoIndex) => (
                                  <tr key={impostoIndex} className="hover:bg-gray-50">
                                    <td className="px-4 py-3">
                                      <div>
                                        <div className="font-semibold text-gray-900 text-sm">{imposto.nome}</div>
                                        <div className="text-xs text-gray-500">{imposto.descricao}</div>
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-700">
                                      R$ {imposto.baseCalculo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-700">
                                      {imposto.aliquota.toFixed(2)}%
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                      <div className="font-semibold text-blue-600 text-sm">
                                        R$ {imposto.valorCalculado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        Total: R$ {(imposto.valorCalculado * premiacao.quantidadePremios).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                      <div className="font-semibold text-green-600 text-sm">
                                        R$ {imposto.valorRecolhido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        Total: R$ {(imposto.valorRecolhido * premiacao.quantidadePremios).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                      <div className={`font-semibold text-sm ${imposto.diferenca > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                        {imposto.diferenca > 0 ? '-' : ''} R$ {Math.abs(imposto.diferenca).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        Total: R$ {(imposto.diferenca * premiacao.quantidadePremios).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                                        imposto.status === 'conforme' ? 'bg-green-100 text-green-700' :
                                        imposto.status === 'atencao' ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-red-100 text-red-700'
                                      }`}>
                                        <i className={`${
                                          imposto.status === 'conforme' ? 'ri-checkbox-circle-fill' :
                                          imposto.status === 'atencao' ? 'ri-error-warning-fill' :
                                          'ri-close-circle-fill'
                                        }`}></i>
                                        {imposto.status === 'conforme' ? 'OK' :
                                         imposto.status === 'atencao' ? 'Atenção' :
                                         'Alerta'}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                                <tr>
                                  <td colSpan={3} className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                                    Totais ({publico}):
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    <div className="font-bold text-blue-600 text-sm">
                                      R$ {totalCalculado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      Total: R$ {(totalCalculado * premiacao.quantidadePremios).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    <div className="font-bold text-green-600 text-sm">
                                      R$ {totalRecolhido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      Total: R$ {(totalRecolhido * premiacao.quantidadePremios).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    <div className={`font-bold text-sm ${totalDiferenca > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                      {totalDiferenca > 0 ? '-' : ''} R$ {Math.abs(totalDiferenca).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      Total: R$ {(totalDiferenca * premiacao.quantidadePremios).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <div className="font-bold text-purple-600 text-sm">
                                      {percentualConformidade.toFixed(1)}%
                                    </div>
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>

                          {/* Alertas e Recomendações */}
                          {statusGeral !== 'conforme' && (
                            <div className={`p-4 border-t ${
                              statusGeral === 'atencao' ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'
                            }`}>
                              <div className="flex items-start gap-3">
                                <i className={`${
                                  statusGeral === 'atencao' ? 'ri-error-warning-line text-yellow-600' : 'ri-alert-line text-red-600'
                                } text-xl mt-0.5`}></i>
                                <div className="flex-1">
                                  <h5 className={`font-semibold mb-2 ${
                                    statusGeral === 'atencao' ? 'text-yellow-900' : 'text-red-900'
                                  }`}>
                                    {statusGeral === 'atencao' ? 'Pontos de Atenção' : 'Alertas Críticos'}
                                  </h5>
                                  <ul className={`space-y-1 text-sm ${
                                    statusGeral === 'atencao' ? 'text-yellow-800' : 'text-red-800'
                                  }`}>
                                    {totalDiferenca > 0 && (
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line mt-0.5"></i>
                                        <span>
                                          Diferença de R$ {(totalDiferenca * premiacao.quantidadePremios).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} entre impostos calculados e recolhidos
                                        </span>
                                      </li>
                                    )}
                                    {percentualConformidade < 95 && (
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line mt-0.5"></i>
                                        <span>
                                          Conformidade de {percentualConformidade.toFixed(1)}% - Recomenda-se revisão dos recolhimentos
                                        </span>
                                      </li>
                                    )}
                                    <li className="flex items-start gap-2">
                                      <i className="ri-arrow-right-s-line mt-0.5"></i>
                                      <span>
                                        Verificar obrigações acessórias (eSocial, DIRF, Informe de Rendimentos)
                                      </span>
                                    </li>
                                    {statusGeral === 'alerta' && (
                                      <li className="flex items-start gap-2">
                                        <i className="ri-arrow-right-s-line mt-0.5"></i>
                                        <span className="font-semibold">
                                          Ação necessária: Regularizar recolhimentos pendentes para evitar multas e juros
                                        </span>
                                      </li>
                                    )}
                                  </ul>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Conformidade OK */}
                          {statusGeral === 'conforme' && (
                            <div className="p-4 bg-green-50 border-t border-green-200">
                              <div className="flex items-center gap-3">
                                <i className="ri-checkbox-circle-line text-green-600 text-xl"></i>
                                <div>
                                  <h5 className="font-semibold text-green-900 mb-1">Conformidade Adequada</h5>
                                  <p className="text-sm text-green-800">
                                    Os impostos estão sendo recolhidos corretamente. Mantenha o acompanhamento das obrigações acessórias.
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Resumo Final de Conformidade */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
              <h3 className="font-bold text-gray-900 text-lg mb-4 flex items-center gap-2">
                <i className="ri-file-shield-line text-purple-600 text-xl"></i>
                Resumo de Conformidade Geral
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-3xl font-bold text-blue-600 mb-1">
                    R$ {resultado.totalImpostos.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </div>
                  <div className="text-sm text-gray-600">Total Calculado</div>
                </div>
                
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-3xl font-bold text-green-600 mb-1">
                    R$ {(resultado.totalImpostos * 0.95).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </div>
                  <div className="text-sm text-gray-600">Total Recolhido</div>
                </div>
                
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <div className="text-3xl font-bold text-orange-600 mb-1">
                    R$ {(resultado.totalImpostos * 0.05).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </div>
                  <div className="text-sm text-gray-600">Diferença a Regularizar</div>
                </div>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <i className="ri-lightbulb-line text-purple-600 text-xl mt-0.5"></i>
                  <div className="flex-1">
                    <h4 className="font-semibold text-purple-900 mb-2">Recomendações</h4>
                    <ul className="space-y-2 text-sm text-purple-800">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-purple-600 mt-0.5"></i>
                        <span>Regularizar os R$ {(resultado.totalImpostos * 0.05).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} de diferença identificada</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-purple-600 mt-0.5"></i>
                        <span>Verificar cumprimento de todas as obrigações acessórias (eSocial, DIRF, Informe de Rendimentos)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-purple-600 mt-0.5"></i>
                        <span>Manter documentação fiscal organizada para eventuais fiscalizações</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-purple-600 mt-0.5"></i>
                        <span>Revisar periodicamente os cálculos tributários para garantir conformidade contínua</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Botões de Ação */}
            <div className="flex justify-between gap-3">
              <button
                onClick={() => setStep(2)}
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 whitespace-nowrap"
              >
                Voltar
              </button>
              <button
                onClick={() => setStep(4)}
                className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 whitespace-nowrap"
              >
                Exportar Relatório
              </button>
            </div>
          </div>
        )}

        {/* Etapa 4 - Exportação */}
        {step === 4 && resultado && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-file-download-line text-white text-4xl"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Simulação Concluída!</h2>
                <p className="text-gray-600">Escolha como deseja exportar o relatório</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <button
                  onClick={gerarPDF}
                  className="p-6 border-2 border-gray-200 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-all group"
                >
                  <div className="w-16 h-16 bg-red-100 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-red-200">
                    <i className="ri-file-pdf-line text-red-600 text-3xl"></i>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Exportar PDF</h3>
                  <p className="text-sm text-gray-600">Relatório completo em PDF</p>
                </button>

                <button
                  onClick={gerarExcel}
                  className="p-6 border-2 border-gray-200 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-all group"
                >
                  <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200">
                    <i className="ri-file-excel-line text-green-600 text-3xl"></i>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Exportar Excel</h3>
                  <p className="text-sm text-gray-600">Planilha editável</p>
                </button>

                <button
                  onClick={() => alert('Funcionalidade de envio por email será implementada em breve!')}
                  className="p-6 border-2 border-gray-200 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-all group"
                >
                  <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200">
                    <i className="ri-mail-send-line text-blue-600 text-3xl"></i>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Enviar por Email</h3>
                  <p className="text-sm text-gray-600">Compartilhar relatório</p>
                </button>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="font-semibold text-gray-900 mb-4">Resumo da Simulação</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Campanha</div>
                    <div className="text-sm font-medium text-gray-900">{campanhasMock.find(c => c.id === config.campanhaId)?.nome}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Quantidade</div>
                    <div className="text-sm font-medium text-gray-900">{config.premiacoes.reduce((sum, p) => sum + p.quantidadePremios, 0)} premiado(s)</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Custo Total</div>
                    <div className="text-sm font-medium text-purple-600">
                      R$ {resultado.custoFinalEmpresa.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Botões de Ação */}
            <div className="flex justify-between gap-3">
              <button
                onClick={() => setStep(3)}
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 whitespace-nowrap"
              >
                Voltar
              </button>
              <button
                onClick={() => {
                  setStep(1);
                  setResultado(null);
                  setConfig({
                    campanhaId: '',
                    premiacoes: [],
                  });
                }}
                className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 whitespace-nowrap"
              >
                Nova Simulação
              </button>
            </div>
          </div>
        )}

        {/* Modal de Comparação */}
        {comparacao && resultado && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-7xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
                <h2 className="text-xl font-bold text-gray-900">Comparação de Modelos Tributários</h2>
                <button
                  onClick={() => setComparacao(false)}
                  className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg"
                >
                  <i className="ri-close-line text-xl text-gray-500"></i>
                </button>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  {/* Pessoa Física */}
                  <div className="border-2 border-blue-200 rounded-xl p-5 bg-blue-50">
                    <div className="text-center mb-4">
                      <div className="w-14 h-14 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="ri-user-line text-white text-xl"></i>
                      </div>
                      <h3 className="font-bold text-gray-900 text-sm">Pessoa Física</h3>
                    </div>
                    <div className="space-y-2.5">
                      <div>
                        <div className="text-xs text-gray-600 mb-1">Valor Bruto</div>
                        <div className="text-base font-bold text-gray-900">R$ 5.000,00</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-600 mb-1">Impostos (IRRF 27,5%)</div>
                        <div className="text-base font-bold text-red-600">- R$ 506,14</div>
                      </div>
                      <div className="pt-2 border-t border-blue-200">
                        <div className="text-xs text-gray-600 mb-1">Valor Líquido</div>
                        <div className="text-lg font-bold text-green-600">R$ 4.493,86</div>
                      </div>
                      <div className="pt-2 border-t border-blue-200">
                        <div className="text-xs text-gray-600 mb-1">Custo Empresa</div>
                        <div className="text-lg font-bold text-blue-600">R$ 5.000,00</div>
                      </div>
                    </div>
                  </div>

                  {/* Pessoa Jurídica */}
                  <div className="border-2 border-purple-200 rounded-xl p-5 bg-purple-50">
                    <div className="text-center mb-4">
                      <div className="w-14 h-14 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="ri-building-line text-white text-xl"></i>
                      </div>
                      <h3 className="font-bold text-gray-900 text-sm">Pessoa Jurídica</h3>
                    </div>
                    <div className="space-y-2.5">
                      <div>
                        <div className="text-xs text-gray-600 mb-1">Valor Bruto</div>
                        <div className="text-base font-bold text-gray-900">R$ 5.000,00</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-600 mb-1">Impostos (11,33%)</div>
                        <div className="text-base font-bold text-red-600">- R$ 566,50</div>
                      </div>
                      <div className="pt-2 border-t border-purple-200">
                        <div className="text-xs text-gray-600 mb-1">Valor Líquido</div>
                        <div className="text-lg font-bold text-green-600">R$ 4.433,50</div>
                      </div>
                      <div className="pt-2 border-t border-purple-200">
                        <div className="text-xs text-gray-600 mb-1">Custo Empresa</div>
                        <div className="text-lg font-bold text-purple-600">R$ 5.000,00</div>
                      </div>
                    </div>
                  </div>

                  {/* MEI */}
                  <div className="border-2 border-green-200 rounded-xl p-5 bg-green-50">
                    <div className="text-center mb-4">
                      <div className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="ri-store-line text-white text-xl"></i>
                      </div>
                      <h3 className="font-bold text-gray-900 text-sm">MEI</h3>
                    </div>
                    <div className="space-y-2.5">
                      <div>
                        <div className="text-xs text-gray-600 mb-1">Valor Bruto</div>
                        <div className="text-base font-bold text-gray-900">R$ 5.000,00</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-600 mb-1">Impostos (DAS)</div>
                        <div className="text-base font-bold text-red-600">- R$ 67,00</div>
                      </div>
                      <div className="pt-2 border-t border-green-200">
                        <div className="text-xs text-gray-600 mb-1">Valor Líquido</div>
                        <div className="text-lg font-bold text-green-600">R$ 4.933,00</div>
                      </div>
                      <div className="pt-2 border-t border-green-200">
                        <div className="text-xs text-gray-600 mb-1">Custo Empresa</div>
                        <div className="text-lg font-bold text-green-600">R$ 5.000,00</div>
                      </div>
                    </div>
                  </div>

                  {/* CLT */}
                  <div className="border-2 border-orange-200 rounded-xl p-5 bg-orange-50">
                    <div className="text-center mb-4">
                      <div className="w-14 h-14 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="ri-briefcase-line text-white text-xl"></i>
                      </div>
                      <h3 className="font-bold text-gray-900 text-sm">CLT</h3>
                    </div>
                    <div className="space-y-2.5">
                      <div>
                        <div className="text-xs text-gray-600 mb-1">Valor Bruto</div>
                        <div className="text-base font-bold text-gray-900">R$ 5.000,00</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-600 mb-1">Impostos (IRRF + INSS)</div>
                        <div className="text-base font-bold text-red-600">- R$ 1.419,36</div>
                      </div>
                      <div className="pt-2 border-t border-orange-200">
                        <div className="text-xs text-gray-600 mb-1">Valor Líquido</div>
                        <div className="text-lg font-bold text-green-600">R$ 3.580,64</div>
                      </div>
                      <div className="pt-2 border-t border-orange-200">
                        <div className="text-xs text-gray-600 mb-1">Custo Empresa</div>
                        <div className="text-lg font-bold text-orange-600">R$ 6.000,00</div>
                      </div>
                    </div>
                  </div>

                  {/* Autônomo (RPA) */}
                  <div className="border-2 border-teal-200 rounded-xl p-5 bg-teal-50">
                    <div className="text-center mb-4">
                      <div className="w-14 h-14 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="ri-user-star-line text-white text-xl"></i>
                      </div>
                      <h3 className="font-bold text-gray-900 text-sm">Autônomo (RPA)</h3>
                    </div>
                    <div className="space-y-2.5">
                      <div>
                        <div className="text-xs text-gray-600 mb-1">Valor Bruto</div>
                        <div className="text-base font-bold text-gray-900">R$ 5.000,00</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-600 mb-1">Impostos (IRRF + INSS)</div>
                        <div className="text-base font-bold text-red-600">- R$ 1.056,14</div>
                      </div>
                      <div className="pt-2 border-t border-teal-200">
                        <div className="text-xs text-gray-600 mb-1">Valor Líquido</div>
                        <div className="text-lg font-bold text-green-600">R$ 3.943,86</div>
                      </div>
                      <div className="pt-2 border-t border-teal-200">
                        <div className="text-xs text-gray-600 mb-1">Custo Empresa</div>
                        <div className="text-lg font-bold text-teal-600">R$ 5.000,00</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <i className="ri-lightbulb-line text-yellow-600 text-xl mt-0.5"></i>
                    <div>
                      <h4 className="font-semibold text-yellow-900 mb-1">Recomendação</h4>
                      <p className="text-sm text-yellow-800">
                        Para este cenário, o modelo MEI apresenta o menor custo tributário, 
                        resultando em maior valor líquido para o premiado. No entanto, é importante 
                        verificar se o valor não ultrapassa os limites mensais do MEI.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function getTipoIcon(tipo: string): string {
  switch (tipo) {
    case 'PF':
      return 'ri-user-line';
    case 'PJ':
      return 'ri-building-line';
    case 'MEI':
      return 'ri-store-line';
    case 'CLT':
      return 'ri-briefcase-line';
    case 'AUTONOMO':
      return 'ri-user-star-line';
    case 'REPRESENTANTE':
      return 'ri-user-check-line';
    case 'DISTRIBUIDOR':
      return 'ri-store-2-line';
    default:
      return 'ri-help-circle-line';
  }
}

function getPremiacaoIcon(tipo: string): string {
  switch (tipo) {
    case 'Dinheiro':
      return 'ri-money-dollar-circle-line';
    case 'Cartão pré-pago':
      return 'ri-credit-card-line';
    case 'Crédito digital (wallet)':
      return 'ri-wallet-line';
    case 'Produto físico':
      return 'ri-box-line';
    case 'Gift card':
      return 'ri-gift-line';
    case 'Experiência / serviço':
      return 'ri-star-line';
    case 'Pontos':
      return 'ri-star-half-line';
    case 'Viagem':
      return 'ri-plane-line';
    default:
      return 'ri-help-circle-line';
  }
}
